import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

type Mode = "loading" | "set-password" | "redirecting" | "error";

// Client-side password policy. Server enforces its own floor + (when
// `password_hibp_enabled` is on for the project) a haveibeenpwned breach
// check; this just blocks obvious weak inputs before a round-trip.
function validatePassword(pw: string): string | null {
  if (pw.length < 12) return "Use at least 12 characters.";
  let classes = 0;
  if (/[a-z]/.test(pw)) classes++;
  if (/[A-Z]/.test(pw)) classes++;
  if (/[0-9]/.test(pw)) classes++;
  if (/[^A-Za-z0-9]/.test(pw)) classes++;
  if (classes < 3) return "Mix upper, lower, number, and symbol (any 3 of 4).";
  return null;
}

/**
 * Lands here from invite / password-recovery emails.
 *
 * Supabase JS (`detectSessionInUrl: true`) parses the URL hash automatically
 * and creates a session. We watch for that, then either:
 *   - if hash carried `type=invite` or `type=recovery` → show password form
 *   - otherwise → fast-forward to /dashboard
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Self-service "send me a new link" — most expired-link errors come from
  // Gmail / iOS Mail pre-fetching the action URL and burning the single-use
  // token before the recipient ever taps. Letting them request a fresh one
  // directly here means they don't have to ping their admin.
  const [resendEmail, setResendEmail] = useState("");
  const [resendSent, setResendSent] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Read the type=... param BEFORE the SDK strips the hash.
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const flowType = params.get("type"); // "invite" | "recovery" | "magiclink" | null
    const errorDesc = params.get("error_description") ?? params.get("error");

    if (errorDesc) {
      setErrorMsg(errorDesc.replace(/\+/g, " "));
      setMode("error");
      return;
    }

    // Wait briefly for the SDK to parse the hash + establish session.
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts++;
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        if (flowType === "invite" || flowType === "recovery") {
          setMode("set-password");
        } else {
          setMode("redirecting");
          // Let auth store hydrate, then go.
          setTimeout(() => navigate("/dashboard", { replace: true }), 200);
        }
        return;
      }
      if (attempts > 20) {
        setErrorMsg("Could not establish a session from this link. It may have expired or already been used.");
        setMode("error");
        return;
      }
      setTimeout(tick, 150);
    };
    void tick();

    return () => { cancelled = true; };
  }, [navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    const policyError = validatePassword(password);
    if (policyError) {
      toast.error(policyError);
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      // Resolve the session at submission time and pass its JWT explicitly.
      // A Functions client can otherwise retain the anonymous Authorization
      // header from before the email callback established the session.
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session?.access_token) {
        setErrorMsg("Your setup session expired. Request a new link below to set your password.");
        setMode("error");
        return;
      }
      // Use the server-side admin API to set the password. Calling
      // supabase.auth.updateUser directly fails with "AAL2 session required"
      // on accounts that have MFA enabled, because a recovery-link session is
      // only AAL1. The edge function uses admin.updateUserById which bypasses
      // that check while the recovery JWT still proves inbox ownership.
      const { data: fnData, error: fnError } = await supabase.functions.invoke<{ ok: boolean; error?: string; detail?: string }>(
        "apply-password-reset",
        {
          body: { password },
          headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        },
      );
      if (fnError || !fnData?.ok) {
        let failure = fnData;
        const response = (fnError as { context?: Response } | null)?.context;
        if (response instanceof Response) {
          try { failure = await response.clone().json(); } catch { /* Keep the fallback message. */ }
        }
        if (response?.status === 401 || failure?.error === "invalid_jwt" || failure?.error === "missing_jwt") {
          setErrorMsg("Your setup session expired. Request a new link below to set your password.");
          setMode("error");
          return;
        }
        const detail = failure?.detail ?? failure?.error ?? "Could not save your password. Please try again.";
        toast.error(detail);
        return;
      }
      toast.success("Password set");
      // onAuthStateChange (subscribed in the auth store) will refresh
      // the session and hydrate the CurrentUser. Just navigate.
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Could not save your password. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
        <div className="mb-6 flex items-center gap-2.5">
          <img src="/favicon.svg" alt="" className="h-10 w-10 rounded-lg shadow-sm" />
          <div className="leading-tight">
            <h1 className="text-sm font-bold tracking-tight text-slate-900">Routes Made</h1>
            <p className="text-sm font-extrabold tracking-[0.2em] text-brand-600">SIMPLE</p>
          </div>
        </div>

        {(mode === "loading" || mode === "redirecting") && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
            {mode === "loading" ? "Verifying your link…" : "Signing you in…"}
          </div>
        )}

        {mode === "error" && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Link problem</h2>
            <p className="text-sm text-rose-700">{errorMsg}</p>
            {resendSent ? (
              <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
                Sent. Check your inbox — the new link is good for 1 hour. Open it on the device you want to sign in on.
              </p>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (resending) return;
                  const trimmed = resendEmail.trim().toLowerCase();
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                    toast.error("Enter a valid email.");
                    return;
                  }
                  setResending(true);
                  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
                    redirectTo: `${window.location.origin}/auth/callback`,
                  });
                  setResending(false);
                  if (error) {
                    toast.error(error.message);
                    return;
                  }
                  setResendSent(true);
                }}
                className="space-y-2"
              >
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">
                    Send me a new link
                  </span>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  />
                </label>
                <button
                  type="submit"
                  disabled={resending}
                  className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {resending ? "Sending…" : "Email me a new link"}
                </button>
              </form>
            )}
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="w-full rounded-md bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
            >
              Go to sign in
            </button>
          </div>
        )}

        {mode === "set-password" && (
          <>
            <h2 className="text-lg font-semibold text-slate-900">Set your password</h2>
            <p className="mt-1 text-sm text-slate-500">
              Welcome. Choose a password to finish setting up your account.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  At least 12 characters with a mix of upper, lower, number, and symbol.
                </span>
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Confirm password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Save and continue"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
